






















































































































































import { mapActions, mapGetters } from 'vuex';

import moment from 'moment';
import mixins from '@/utils/mixins';
import snackbar from '@/mixins/snackbar';
import Functions from '@/mixins/functions';
import auth from '@/mixins/auth';
import { merge, omit } from 'lodash';

import {
  ErrorManager,
  IDocumentRevision,
  IQuoteProductDocument,
  QuoteProductDocument
} from '@/models';
import { createPdf } from '@/utils/helpers';
import { IFetchQuoteProductDocumentsParams } from '@/services/document/document.types';

import ALPage from '@/components/core/Page/ALPage.vue';
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import DocumentDetailFormDialog, {
  FormType
} from '@/components/document/DocumentDetailFormDialog.vue';
import DocumentNewRevisionFormDialog from '@/components/document/DocumentNewRevisionFormDialog.vue';
import DocumentList from '@/components/document/DocumentList.vue';
import DocumentRevisionHistoryDialog from '@/components/document/DocumentRevisionHistoryDialog.vue';
import DocumentDetailPDFViewerDialog from '@/components/document/DocumentDetailPDFViewerDialog.vue';
import DocumentSearchFilter from '@/components/document/DocumentSearchFilter.vue';

const baseMixins = mixins(auth, snackbar, Functions);

interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    documentRevisionDialog: InstanceType<typeof DocumentRevisionHistoryDialog>;
    documentDetailFormDialog: InstanceType<typeof DocumentDetailFormDialog>;
    documentNewRevisionFormDialog: InstanceType<
      typeof DocumentNewRevisionFormDialog
    >;
    documentDetailPDFViewer: InstanceType<typeof DocumentDetailPDFViewerDialog>;
  };
}

export default baseMixins
  .extend<Options>({ functional: false })
  .extend({
    components: {
      ALPage,
      ErrorAlert,
      ConfirmationDialog,
      DocumentDetailFormDialog,
      DocumentList,
      DocumentRevisionHistoryDialog,
      DocumentDetailPDFViewerDialog,
      DocumentNewRevisionFormDialog,
      DocumentSearchFilter
    },

    data() {
      return {
        loading: false,
        search: '',

        errorMessage: '',
        detailErrorMessage: '',

        selectedDocument: null as null | IQuoteProductDocument,
        deleteDialog: false,
        deleteInProgress: false,
        deleteErrorMessage: '',
        rightDrawer: true,

        isSearchFilterActive: false,
        params: {
          search: this.$route.query.search as string,
          page: Number(this.$route.query.page ?? 1)
        } as IFetchQuoteProductDocumentsParams
      };
    },

    computed: {
      ...mapGetters({
        getQuoteProductDocuments: 'document/getQuoteProductDocuments',
        numDocuments: 'document/getDocumentCount'
      }),

      documents(): IQuoteProductDocument[] {
        return this.getQuoteProductDocuments;
      },

      extraFiltersApplied(): boolean {
        return Object.keys(omit(this.$route.query, ['page', 'search'])).some(
          v => !!v
        );
      }
    },

    watch: {
      'params.search'(search: string) {
        this.debounceFetchQuoteProductDocuments({
          ...this.params,
          search: search,
          page: 1
        }).then(() => {
          this.params.page = 1;
        });
      }
    },

    async mounted() {
      merge(this.params, omit(this.$route.query, ['page', 'search']));

      this.loading = true;
      await this.searchQuoteProductDocuments(this.params);
    },

    methods: {
      moment,

      ...mapActions({
        fetchQuoteProductDocuments: 'document/fetchQuoteProductDocuments',
        deleteQuoteProductDocument: 'document/deleteQuoteProductDocument'
      }),

      refreshSearchFilter(params: IFetchQuoteProductDocumentsParams) {
        this.clearAllErrors();

        this.loading = true;
        this.searchQuoteProductDocuments({
          page: 1,
          search: this.params.search,
          ...params
        }).then(() => {
          // update params object
          this.params.quote_document__document__tags =
            params.quote_document__document__tags;
          this.params.page = 1;
        });
      },

      async debounceFetchQuoteProductDocuments(
        params: IFetchQuoteProductDocumentsParams
      ) {
        this.clearAllErrors();
        this.loading = true;

        return this.debounce(this.searchQuoteProductDocuments, 1000)(params);
      },

      searchQuoteProductDocuments(params: IFetchQuoteProductDocumentsParams) {
        /* this will update the URL query based on param values. */
        this.$router
          .replace({
            name: 'document-marketing-list',
            query: params as Record<string, any>
          })
          .catch((e: Error) => e); //catch navigation duplication error

        return this.fetchQuoteProductDocuments(params)
          .catch(error => {
            this.errorMessage = ErrorManager.extractApiError(error);
          })
          .finally(() => {
            this.loading = false;
          });
      },

      clearAllErrors() {
        this.errorMessage = '';
        this.detailErrorMessage = '';
      },

      openDocumentDetailFormDialog(
        document?: QuoteProductDocument,
        formType?: FormType
      ) {
        const documentDetailFormDialog = this.$refs.documentDetailFormDialog;

        documentDetailFormDialog.open(document, formType);
      },

      openDocumentNewRevisionFormDialog(document: QuoteProductDocument) {
        const documentNewRevisionFormDialog = this.$refs
          .documentNewRevisionFormDialog;

        documentNewRevisionFormDialog.open(document);
      },

      openDocumentRevisionHistoryDialog(document: QuoteProductDocument) {
        const revisionDialog = this.$refs.documentRevisionDialog;

        revisionDialog.open(document);
      },

      downloadLatestRevisionFile(document: IQuoteProductDocument) {
        this.clearAllErrors();
        this.$services.fileLoader
          .downloadFile(document.quote_document.document.latest_revision!.file)
          .then((data: Blob) => {
            const fileName = `document-#${
              document.quote_document.document.latest_revision!.revision
            }-${moment(
              document.quote_document.document.latest_revision!.uploaded_at
            ).format('YYYY-MM-DD-HH-mm-ss')}`;
            createPdf(data, fileName);

            this.createSnack({
              color: 'success darken-1',
              type: 'success',
              right: true,
              useBtn: false,
              msg: `File attachment download successfully`,
              btnText: 'Ok',
              timeout: 6000
            });
          })
          // Not to display error
          .catch((error: any) => {
            this.errorMessage = ErrorManager.extractApiError(error);
            if (error.response.data.detail) {
              this.detailErrorMessage = error.response.data.detail;
            }
          })
          .finally(() => {
            this.loading = false;
          });
      },

      openDocumentDeleteDialog(document: IQuoteProductDocument) {
        this.selectedDocument = document;
        this.deleteDialog = true;
      },

      openDocumentRevisionDetailPDFViewer(
        document: IQuoteProductDocument,
        documentRevision: IDocumentRevision
      ) {
        const documentDetailPDFViewer = this.$refs.documentDetailPDFViewer;

        documentDetailPDFViewer.open(document, documentRevision);
      },

      closeDocumentDeleteDialog() {
        this.deleteDialog = false;
      },

      removeDocument(document: IQuoteProductDocument) {
        this.deleteInProgress = true;

        this.deleteQuoteProductDocument(document.id)
          .then(() => {
            this.closeDocumentDeleteDialog();
          })
          .catch(error => {
            this.deleteErrorMessage = ErrorManager.extractApiError(error);
          })
          .finally(() => {
            this.deleteInProgress = false;
          });
      },

      onUpdatePage(newPage: number) {
        if (newPage != this.params.page) {
          this.searchQuoteProductDocuments({
            ...this.params,
            page: newPage
          }).then(() => {
            this.params.page = newPage;
          });
        }
      }
    }
  });
